/**
 * Notification kinds, categories, channels, and delivery states.
 *
 * A Notification communicates a domain or security event. **It never creates one.** The Planning
 * Workspace is authoritative; an email that says a revision was requested is a copy of a fact that
 * lives elsewhere, and an older email never supersedes what the workspace shows now (L2-08 §5,
 * L3-25 §2).
 *
 * Kind identifiers are **stable and language-neutral** (L3-25 §5). They are persisted in
 * `notifications.kind`, so renaming one strands every historical row — treat a kind like a database
 * column: add and deprecate, never rename in place.
 */

// ---------------------------------------------------------------------------
// Category
// ---------------------------------------------------------------------------

/**
 * What sort of message this is, which decides whether a preference may silence it.
 *
 *  planning   — the ordinary workflow: submitted, revision requested, approved. Suppressible.
 *  security   — password changed, address changed, access revoked. **Never suppressible**, because
 *               the person who most needs to see it is the one whose Account was taken over, and an
 *               attacker with the session would simply switch the preference off (L2-08 §28).
 *  governance — ownership transferred, administrator added. Scoped to those with a need to know.
 *  operational— export ready, and other things the requester asked for themselves.
 */
export const NOTIFICATION_CATEGORIES = [
  'planning',
  'security',
  'governance',
  'operational',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

// ---------------------------------------------------------------------------
// Channel
// ---------------------------------------------------------------------------

/**
 * Version 1 delivers in-app and by email (L3-25 §3). Web push and SMS are named nowhere in the
 * schema so that adding one later is an addition rather than a redefinition.
 */
export const NOTIFICATION_CHANNELS = ['in_app', 'email'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

// ---------------------------------------------------------------------------
// Delivery state
// ---------------------------------------------------------------------------

/**
 * Per recipient, not per notification: one Notification fans out to several people and each is
 * tracked independently (L3-25 §11, §13).
 *
 * `delivered` means the provider accepted it. It does **not** mean anybody read it — provider
 * telemetry is weaker than it looks and overstating it would make the history lie (L3-25 §14).
 */
export const NOTIFICATION_DELIVERY_STATES = [
  'pending',
  'queued',
  'delivering',
  'delivered',
  'temporarily_failed',
  'failed',
  'suppressed',
  'cancelled',
] as const;
export type NotificationDeliveryState = (typeof NOTIFICATION_DELIVERY_STATES)[number];

const TERMINAL: ReadonlySet<NotificationDeliveryState> = new Set([
  'delivered',
  'failed',
  'suppressed',
  'cancelled',
]);

export function isTerminalDeliveryState(state: NotificationDeliveryState): boolean {
  return TERMINAL.has(state);
}

/** Worth showing to the person who asked for the message to go out (L2-08 §41). */
export function isVisibleFailure(state: NotificationDeliveryState): boolean {
  return state === 'failed';
}

// ---------------------------------------------------------------------------
// Kinds
// ---------------------------------------------------------------------------

export const NOTIFICATION_KINDS = [
  // account · security
  'account.verify_email',
  'account.password_reset',
  'account.password_changed',

  // capabilities
  'capability.shared',
  'capability.revoked',
  'collaborator.added',

  // planning workflow
  'planning.contribution_submitted',
  'planning.revision_requested',
  'planning.contribution_approved',
  'planning.contribution_rejected',

  // liturgy lifecycle
  'liturgy.finalized',
  'liturgy.reopened',

  // operational
  'export.ready',
] as const;

export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export function isNotificationKind(value: string): value is NotificationKind {
  return (NOTIFICATION_KINDS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------

/**
 * The preference switches an Account may set. Deliberately coarse: a switch per workflow concern
 * rather than per kind, because a preference centre with thirteen toggles is one nobody configures.
 *
 * There is **no key for the security category** — not a hidden default, an absence. A preference
 * that cannot be expressed cannot be honoured by mistake.
 */
export const NOTIFICATION_PREFERENCE_KEYS = [
  'planning_activity',
  'review_outcomes',
  'lifecycle',
  'exports',
] as const;
export type NotificationPreferenceKey = (typeof NOTIFICATION_PREFERENCE_KEYS)[number];

export type NotificationPreferences = Readonly<Record<NotificationPreferenceKey, boolean>>;

/**
 * Defaults answer L2-08's open questions 3 and 4 the way the workflow implies: a Revision Request
 * asks somebody to do something, so it is on; an Approval is pleasant news that arrives in batches,
 * so it is on but is the first thing a busy administrator turns off.
 */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  planning_activity: true,
  review_outcomes: true,
  lifecycle: true,
  exports: false,
};

/**
 * Reads stored preferences, which are jsonb and therefore of no guaranteed shape. Anything missing
 * or malformed falls back to the default rather than to `false`: a corrupted row must not silently
 * stop a Revision Request from reaching the person it is addressed to.
 */
export function readPreferences(value: unknown): NotificationPreferences {
  if (typeof value !== 'object' || value === null) return DEFAULT_NOTIFICATION_PREFERENCES;

  const record = value as Record<string, unknown>;
  const result: Record<string, boolean> = {};

  for (const key of NOTIFICATION_PREFERENCE_KEYS) {
    result[key] =
      typeof record[key] === 'boolean' ? record[key] : DEFAULT_NOTIFICATION_PREFERENCES[key];
  }

  return result as NotificationPreferences;
}
